/**
 * @file AdherenceView.jsx
 * @author Daniel Aguirre
 * @description Adherence View Page
 */

// Dependencies
import React from 'react';
import PropTypes from 'prop-types';
import {
  CalendarOutlined,
  OrderedListOutlined,
  PhoneFilled
  // PhoneFilled
} from '@ant-design/icons';
import { useHistory } from 'react-router-dom';
import { Button, Empty } from 'antd';
import moment from 'moment';

// Components
import AppCardLayout from '@nephro/components/AppCardLayout';

// Helpers
import { getStorageItem } from '@nephro/commons/helpers/storage-helper';
import { onGetAdherence } from '@nephro/commons/helpers/utils';
import { getPatientStage } from '../helpers/utils';
import { ADHERENCE_INDICATOR, STAGE_INDICATOR } from '../helpers/constants';
import usePatientIndicators from '../helpers/usePatientIndicators';

// Styles
import styles from './styles.module.css';

function AdherenceView({ patientActions, actions, clinicPhonesActions }) {
  const [adherenceIndicator] = usePatientIndicators(ADHERENCE_INDICATOR);
  const [stageIndicator] = usePatientIndicators(STAGE_INDICATOR);
  const [currentAdherence, setCurrentAdherence] = React.useState(null);
  const [patientData, setPatientData] = React.useState(0);
  const [clinicPhones, setClinicPhones] = React.useState([]);
  const history = useHistory();

  const { getClinicPhonesByClinicName } = clinicPhonesActions;
  const { getPatientExtraDataById } = patientActions;
  const { setLoading } = actions;

  const onGetClinicPhones = (_, response) => {
    if (response?.data) {
      setClinicPhones(response.data);
    }
  };

  //  Function to get and set patient full info
  const onGetFullPatientInfo = (_, response) => {
    if (response?.data) {
      const clinicName = response.data?.aseguradora;
      // TODO: get phones
      getClinicPhonesByClinicName({ clinicName }, onGetClinicPhones);

      setPatientData({
        ...response?.data
      });
      setLoading(false);
    }
  };

  const onGetProfileInfo = async () => {
    const patientInf = await getStorageItem('patientInfo');
    if (patientInf?.id_patient) {
      getPatientExtraDataById(
        { id: patientInf.id_patient },
        onGetFullPatientInfo
      );
    }
  };

  const onGetMonthsInterval = (stage) => {
    let adherence = 'el último mes';

    switch (stage) {
      case '5':
        adherence = 'el último mes.';
        return adherence;
      case '4':
        adherence = 'los últimos 3 meses.';
        return adherence;
      default:
        adherence = 'los últimos 6 meses.';
        return adherence;
    }
  };

  React.useEffect(() => {
    onGetProfileInfo();
    if (stageIndicator && adherenceIndicator !== undefined) {
      const patientStage = getPatientStage(stageIndicator.valor);
      setCurrentAdherence(
        onGetAdherence(patientStage, adherenceIndicator?.fecha)
      );
    }
  }, [stageIndicator]);

  const adherenceValidation = currentAdherence === 'es Buena';

  return (
    <AppCardLayout>
      <div className={styles.container}>
        <h1 className={styles.title}>
          <OrderedListOutlined className={styles.icon} />
          Asistencia a mis controles
        </h1>
        {adherenceIndicator === undefined ? (
          <div className="mt-40">
            <Empty
              style={{ color: '#858fab', fontSize: 15 }}
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="No hay registro de datos"
            />
          </div>
        ) : (
          <section>
            <h3 className="text-left mb-10 text-baxterGray text-base">
              {stageIndicator && adherenceValidation
                ? `Fuiste valorado por el programa de Nefroprotección en ${onGetMonthsInterval(
                    getPatientStage(stageIndicator?.valor)
                  )} Sigue programando tus controles oportunamente.`
                : `No fuiste valorado por el programa de Nefroprotección en ${onGetMonthsInterval(
                    getPatientStage(stageIndicator?.valor)
                  )} Programa tu consulta.`}
            </h3>
            <section className="w-full flex justify-center">
              <section
                className="p-1 text-left mb-10 border border-gray-400 w-full md:w-1/2"
                style={{ borderRadius: 10 }}>
                <span className="ml-2">Tu última cita fue: </span>
                <strong className="float-right mr-2">
                  {moment(adherenceIndicator?.fecha)
                    .utc(false)
                    .format('DD/MM/YYYY')}
                </strong>
              </section>
            </section>
            <h3 className="text-left mb-4 text-baxterGray text-base">
              Comunícate con tu centro de atención médica
              <strong> {patientData?.aseguradora ?? 'NA'}</strong>
            </h3>
            <ul className="text-left space-y-3 ml-2">
              {clinicPhones.map(({ phones }, index) => (
                <li key={index}>
                  <PhoneFilled className="mr-3" />
                  <a
                    className="align-text-top text-baxterGray"
                    href={`tel:${phones}`}>
                    {phones}
                  </a>
                </li>
              ))}
            </ul>
            <section>
              <Button
                onClick={() => history.push('/main/appointments')}
                size="large"
                className="w-full text-center mt-5"
                style={{ maxWidth: 400 }}
                type="primary">
                <span>
                  <CalendarOutlined
                    style={{ fontSize: 20, verticalAlign: 'baseline' }}
                  />
                  <span className="ml-3">Agendar nueva cita</span>
                </span>
              </Button>
            </section>
          </section>
        )}
      </div>
    </AppCardLayout>
  );
}

/**
 * Component definition of proptypes.
 */
AdherenceView.propTypes = {
  actions: PropTypes.shape({
    setLoading: PropTypes.func.isRequired
  }).isRequired,
  patientActions: PropTypes.shape({
    getPatientExtraDataById: PropTypes.func.isRequired
  }).isRequired,
  clinicPhonesActions: PropTypes.shape({
    getClinicPhonesByClinicName: PropTypes.func.isRequired
  }),
  loading: PropTypes.bool
};

export default AdherenceView;
